/**
 * useYogAnalytics.js
 *
 * Centralised analytics hook for the Yog (Online Yoga) section.
 * Wraps GA4 via react-ga4 — mirrors the same IS_PRODUCTION guard
 * already used in App.js so events never fire on localhost/dev.
 *
 * HOW TO USE:
 *   const analytics = useYogAnalytics();
 *   analytics.trackVideoPlay(videoId, videoName, category, duration);
 *
 * DEV MODE:
 *   All events are console.group'd with the [YogAnalytics] prefix so you
 *   can verify payloads without opening the GA4 dashboard.
 *   Open browser DevTools → Console to see all events in real time.
 *
 * FIX NOTES:
 *   1. IS_PRODUCTION now also excludes 127.0.0.1 (not just localhost)
 *   2. Dev logging uses console.group + console.table for readable output
 *   3. trackSearchQuery debounce reduced to 600ms for snappier dev feedback
 */

import { useCallback, useRef } from "react";
import ReactGA from "react-ga4";

// ─── Production guard ──────────────────────────────────────────────────────────
// FIX: added 127.0.0.1 exclusion — CRA dev server can serve on either hostname
const IS_PRODUCTION =
  process.env.NODE_ENV === "production" &&
  typeof window !== "undefined" &&
  window.location.hostname !== "localhost" &&
  window.location.hostname !== "127.0.0.1";

// ─── Core send helper ──────────────────────────────────────────────────────────
const send = (eventName, params = {}) => {
  // Strip undefined / null / empty values so GA4 payload stays clean
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );

  if (IS_PRODUCTION) {
    ReactGA.event(eventName, cleanParams);
  } else {
    // FIX: use console.group + console.table for much easier dev inspection
    // eslint-disable-next-line no-console
    console.group(`%c[YogAnalytics] ${eventName}`, "color:#3d6b4f;font-weight:bold;");
    // eslint-disable-next-line no-console
    if (Object.keys(cleanParams).length > 0) console.table(cleanParams);
    // eslint-disable-next-line no-console
    else console.log("(no params)");
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
};

// ─── Simple debounce (avoids lodash dependency) ────────────────────────────────
const debounce = (fn, delay) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};

// ═══════════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════════

const useYogAnalytics = () => {
  // FIX: debounce reduced 800ms → 600ms for snappier dev feedback
  const debouncedSearchRef = useRef(
    debounce((query, resultsCount) => {
      if (!query.trim()) return;
      send("yog_search_query", {
        search_term: query,
        results_count: resultsCount,
      });
      if (resultsCount === 0) {
        send("yog_search_no_results", { search_term: query });
      }
    }, 600)
  );

  // ── Category navigation ──────────────────────────────────────────────────────

  const trackCategorySelect = useCallback((categoryName, categoryIndex) => {
    send("yog_category_select", {
      category_name: categoryName,
      category_index: categoryIndex,
    });
  }, []);

  const trackGroupSelect = useCallback((groupName, level, categoryName) => {
    send("yog_group_select", {
      group_name:    groupName,
      level,
      category_name: categoryName,
    });
  }, []);

  // ── Video playback ───────────────────────────────────────────────────────────

  const trackVideoPlay = useCallback(
    (videoId, videoName, category, duration, source = "sidebar") => {
      send("yog_video_play", {
        video_id:    videoId,
        video_name:  videoName,
        category,
        duration,
        play_source: source,
      });
    },
    []
  );

  const trackThumbnailClick = useCallback((videoId, videoName, indexInGroup) => {
    send("yog_thumbnail_click", {
      video_id:       videoId,
      video_name:     videoName,
      thumb_position: indexInGroup,
    });
  }, []);

  const trackMobileVideoClick = useCallback((videoId, videoName, groupName) => {
    send("yog_mobile_video_click", {
      video_id:   videoId,
      video_name: videoName,
      group_name: groupName,
    });
  }, []);

  // ── Accordion / detail sections ──────────────────────────────────────────────

  const trackAccordionOpen = useCallback((label, videoName) => {
    send("yog_accordion_open", {
      accordion_label: label,
      video_name:      videoName,
    });
  }, []);

  // ── Search ───────────────────────────────────────────────────────────────────

  const trackSearchOpen = useCallback(() => {
    send("yog_search_open");
  }, []);

  const trackSearchQuery = useCallback((query, resultsCount) => {
    debouncedSearchRef.current(query, resultsCount);
  }, []);

  const trackTagFilter = useCallback((tag, resultsCount, isActivating) => {
    if (isActivating) {
      send("yog_tag_filter", {
        tag_name:      tag,
        results_count: resultsCount,
      });
    }
  }, []);

  const trackSearchResultClick = useCallback(
    (item, query, activeTag, position) => {
      send("yog_search_result_click", {
        video_id:        item.videoPath,
        video_name:      item.name,
        search_term:     query   || null,
        active_tag:      activeTag || null,
        result_position: position,
        category:        item.category,
        group_name:      item.groupName,
      });
    },
    []
  );

  const trackSearchClear = useCallback(() => {
    send("yog_search_clear");
  }, []);

  // ── Scroll-to-player ─────────────────────────────────────────────────────────

  const trackPlayerScroll = useCallback((videoName) => {
    send("yog_player_scroll", { video_name: videoName });
  }, []);

  return {
    trackCategorySelect,
    trackGroupSelect,
    trackVideoPlay,
    trackThumbnailClick,
    trackMobileVideoClick,
    trackAccordionOpen,
    trackSearchOpen,
    trackSearchQuery,
    trackTagFilter,
    trackSearchResultClick,
    trackSearchClear,
    trackPlayerScroll,
  };
};

export default useYogAnalytics;